import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSAPDataStore } from '../store/SAPDataStore';
import { ChartDataService } from '../services/ChartDataService';
import { natsClient } from '../services/NATSClient';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import EfficiencyTab from '../components/tabs/EfficiencyTab';
import OutputTab from '../components/tabs/OutputTab';
import DowntimeTab from '../components/tabs/DowntimeTab';
import RejectsTab from '../components/tabs/RejectsTab';
import MTBFTab from '../components/tabs/MTBFTab';
import FaultsTab from '../components/tabs/FaultsTab';
import Select from 'react-select';
import { styles, cx } from '../styles';
import { useTheme, useThemeClasses } from '../providers/ThemeProvider';

const CompareMachineView: React.FC = () => {
  const { t } = useTranslation();
  const { getMachineById, loading, error, machines, dateRange } = useSAPDataStore();
  const { currentTheme } = useTheme();
  const themeClasses = useThemeClasses();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'efficiency'|'output'|'downtime'|'rejects'|'mtbf'|'faults'>('efficiency');
  const [availableShifts, setAvailableShifts] = useState<Record<string, string[]>>({});
  
  // State for subtab synchronization across all machines
  const [efficiencySubTab, setEfficiencySubTab] = useState<'efficiency' | 'utilization'>('efficiency');
  const [outputSubTab, setOutputSubTab] = useState<'shift' | 'day' | 'week'>('shift');
  const [rejectsSubTab, setRejectsSubTab] = useState<'ratio' | 'rejects' | 'events'>('ratio');
  const [mtbfSubTab, setMtbfSubTab] = useState<'mtbf' | 'speed'>('mtbf');
  const [faultsSubTab, setFaultsSubTab] = useState<'number' | 'duration' | 'status'>('number');
  const [faultsAggType, setFaultsAggType] = useState<'number' | 'percent'>('number');

  // Tab keys and labels
  const tabKeys: { key: typeof activeTab, label: string }[] = [
    { key: 'efficiency', label: t('efficiency') },
    { key: 'output', label: t('output') },
    { key: 'downtime', label: t('downtime') },
    { key: 'rejects', label: t('rejects') },
    { key: 'mtbf', label: t('mtbf') },
    { key: 'faults', label: t('faults') },
  ];

  // Fetch shifts data when component mounts or selectedIds change
  useEffect(() => {
    if (selectedIds.length > 0) {
      // Fetch or compute shifts data for the selected machines
      // This is a placeholder for the actual data fetching logic
      console.log('Fetching shifts for machines:', selectedIds);
    }
  }, [selectedIds]);

  // Prepare options for react-select (only machines with data)
  const machineOptions = machines
    .filter(machine => machine.datenVerfuegbar)
    .map(machine => {
      const id = machine.maschinennummer || machine.maschineBK || machine.machineId || '';
      return {
        value: id,
        label: machine.machineName ? `${machine.machineName} (${id})` : id
      };
    })
    .filter(opt => !!opt.value);

  // Handler for react-select
  const handleSelectChange = (selected: any) => {
    setSelectedIds(selected ? selected.map((s: any) => s.value) : []);
  };

  // Custom react-select styles for theme compatibility
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: currentTheme.colors.background.surface,
      borderColor: state.isFocused ? currentTheme.colors.status.info : currentTheme.colors.border.primary,
      color: currentTheme.colors.text.primary,
      minHeight: '40px',
      '&:hover': {
        borderColor: currentTheme.colors.border.secondary,
      },
      boxShadow: state.isFocused ? `0 0 0 1px ${currentTheme.colors.status.info}` : 'none',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: currentTheme.colors.background.surface,
      border: `1px solid ${currentTheme.colors.border.primary}`,
      boxShadow: currentTheme.isDark 
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? currentTheme.colors.status.info
        : state.isFocused 
          ? currentTheme.colors.interactive.hover
          : 'transparent',
      color: state.isSelected 
        ? currentTheme.colors.text.white
        : currentTheme.colors.text.primary,
      '&:hover': {
        backgroundColor: state.isSelected 
          ? currentTheme.colors.status.info
          : currentTheme.colors.interactive.hover,
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: currentTheme.colors.interactive.hover,
      border: `1px solid ${currentTheme.colors.border.secondary}`,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: currentTheme.colors.text.primary,
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: currentTheme.colors.text.secondary,
      '&:hover': {
        backgroundColor: currentTheme.colors.status.error,
        color: currentTheme.colors.text.white,
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: currentTheme.colors.text.muted,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: currentTheme.colors.text.primary,
    }),
    input: (provided: any) => ({
      ...provided,
      color: currentTheme.colors.text.primary,
    }),
  };

  // Render all charts for the active KPI vertically with Highcharts sync
  const renderChartsForActiveTab = () => {
    // Use a syncGroup name for Highcharts sync (same for all charts in this view)
    const syncGroup = `compare-machine-${activeTab}`;
    
    return (
      <div className={styles.compare.container}>
        {selectedIds.map(id => {
          const shiftsForMachine = availableShifts[id] || [];
          return (
            <div 
              key={id} 
              className={cx(styles.compare.machineSection.container, themeClasses.bgSurface, themeClasses.borderPrimary, 'border')}
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border-primary)',
                transition: 'background-color 0.3s ease, border-color 0.3s ease'
              }}
            >
              <div className={cx(styles.compare.machineSection.title, themeClasses.textPrimary, themeClasses.borderSecondary)}>
                {getMachineById(id)?.machineName || id}
              </div>
              {/* Show NATS connection or shift errors inline */}
              {!natsClient.isConnected() ? (
                <div className={cx(styles.compare.machineSection.error, themeClasses.textError)}>{t('natsNotConnected', 'NATS connection not established. Cannot fetch shifts.')}</div>
              ) : !shiftsForMachine.length ? (
                <div className={cx(styles.compare.machineSection.noData, themeClasses.textSecondary)}>{t('noShiftsAvailable', 'No shifts available for this machine.')}</div>
              ) : (
                <>
                  {activeTab === 'efficiency' && (
                    <EfficiencyTab 
                      machineSerialNo={id} 
                      shifts={shiftsForMachine} 
                      syncGroup={syncGroup}
                      activeSubTab={efficiencySubTab}
                      onActiveSubTabChange={setEfficiencySubTab}
                    />
                  )}
                  {activeTab === 'output' && (
                    <OutputTab 
                      machineSerialNo={id} 
                      shifts={shiftsForMachine} 
                      syncGroup={syncGroup}
                      activeSubTab={outputSubTab}
                      onActiveSubTabChange={setOutputSubTab}
                    />
                  )}
                  {activeTab === 'downtime' && <DowntimeTab machineSerialNo={id} shifts={shiftsForMachine} syncGroup={syncGroup} />}
                  {activeTab === 'rejects' && (
                    <RejectsTab 
                      machineSerialNo={id} 
                      shifts={shiftsForMachine} 
                      syncGroup={syncGroup}
                      activeSubTab={rejectsSubTab}
                      onActiveSubTabChange={setRejectsSubTab}
                    />
                  )}
                  {activeTab === 'mtbf' && (
                    <MTBFTab 
                      machineSerialNo={id} 
                      shifts={shiftsForMachine} 
                      syncGroup={syncGroup}
                      activeSubTab={mtbfSubTab}
                      onActiveSubTabChange={setMtbfSubTab}
                    />
                  )}
                  {activeTab === 'faults' && (
                    <FaultsTab 
                      machineSerialNo={id} 
                      shifts={shiftsForMachine} 
                      syncGroup={syncGroup}
                      activeSubTab={faultsSubTab}
                      onActiveSubTabChange={setFaultsSubTab}
                      aggType={faultsAggType}
                      onAggTypeChange={setFaultsAggType}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const { token } = useUnifiedAuth();

  // Fetch available shifts for each selected machine from NATS
  useEffect(() => {
    const fetchShifts = async () => {
      const shiftMap: Record<string, string[]> = {};
      if (!token) {
        // If no token, set empty shifts and return
        selectedIds.forEach(id => {
          shiftMap[id] = [];
        });
        setAvailableShifts(shiftMap);
        return;
      }
      for (const id of selectedIds) {
        const machine = getMachineById(id);
        if (!machine) {
          shiftMap[id] = [];
          continue;
        }
        try {
          const params = {
            serialno: id,
            date_from: dateRange.start,
            date_to: dateRange.end
          };
          const response = await ChartDataService.fetchAvailableShifts(params, token);
          // Response format: { success: true, data: [{shift_name, machine_sn, exist_SAP}, ...] }
          // Handle missing data as empty array
          if (response.success) {
            const shifts = Array.isArray(response.data) ? response.data : [];
            shiftMap[id] = shifts.map((item: { shift_name: string }) => item.shift_name);
          } else {
            shiftMap[id] = [];
          }
        } catch (e) {
          console.error('[CompareMachineView] Error requesting shifts for', id, e);
          shiftMap[id] = [];
        }
      }
      setAvailableShifts(shiftMap);
    };
    if (selectedIds.length > 0) {
      fetchShifts();
    } else {
      setAvailableShifts({});
    }
  }, [selectedIds, dateRange, getMachineById, token]);

  if (loading) {
    return <div className={cx(styles.compare.loading, themeClasses.textSecondary)}>{t('loading', 'Loading data...')}</div>;
  }
  if (error) {
    return <div className={cx(styles.compare.error, themeClasses.textError)}>{t('errorLoadingData', 'Error loading data:')} {error}</div>;
  }

  return (
    <div 
      className={styles.compare.layout.container}
      style={{
        backgroundColor: 'var(--bg-primary)',
        transition: 'background-color 0.3s ease'
      }}
    >
      <h1 
        className={cx(styles.compare.layout.title, themeClasses.textPrimary)}
        style={{
          color: 'var(--text-primary)',
          transition: 'color 0.3s ease'
        }}
      >
        {t('compareMachine')}
      </h1>
      <div className={styles.compare.header.container}>
        <div className={cx(styles.compare.header.title, themeClasses.textSecondary)}>{t('selectMachinesToCompare', 'Select up to 3 machines:')}</div>
        <Select
          isMulti
          options={machineOptions}
          value={machineOptions.filter(opt => selectedIds.includes(opt.value))}
          onChange={handleSelectChange}
          className={styles.compare.header.selector}
          styles={selectStyles}
          placeholder={t('selectMachines', 'Type to search machines...')}
          closeMenuOnSelect={false}
          maxMenuHeight={300}
          isClearable
          menuPlacement="auto"
        />
      </div>
      {selectedIds.length >= 2 ? (
        <>
          {/* Tab selector for comparison type */}
          <div className={styles.compare.tabs.container}>
            {tabKeys.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`${styles.compare.tabs.tab.base} ${activeTab === tab.key ? cx(styles.compare.tabs.tab.active, themeClasses.textInfo, themeClasses.borderAccent) : cx(styles.compare.tabs.tab.inactive, themeClasses.textSecondary, themeClasses.hover)}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Comparison charts vertically for the active KPI with sync */}
          <div className={styles.compare.section.container}>
            <div 
              className={cx(styles.compare.info.container, themeClasses.bgSecondary, themeClasses.borderSecondary, themeClasses.textSecondary)}
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-secondary)',
                transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease'
              }}
            >
              <span className={cx(styles.compare.info.title, themeClasses.textPrimary)}>📊 Synchronized View:</span> Charts are synchronized for {tabKeys.find(tab => tab.key === activeTab)?.label}. 
              Hover over any chart to see synchronized tooltips and crosshairs across all machines.
              {(activeTab === 'efficiency' || activeTab === 'rejects' || activeTab === 'mtbf' || activeTab === 'faults') && (
                <span className={styles.compare.info.subtitle}>
                  <span className={cx(styles.compare.info.title, themeClasses.textPrimary)}>🔄 Subtab Sync:</span> Switching between subtabs will synchronize across all machines.
                </span>
              )}
            </div>
          </div>
          {renderChartsForActiveTab()}
        </>
      ) : (
        <div className={cx(styles.compare.emptyState, themeClasses.textSecondary)}>{t('selectAtLeastTwoMachines', 'Please select at least two machines to compare.')}</div>
      )}
    </div>
  );
};

export default CompareMachineView;
